'use client';

import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const toggleGroupVariants = cva(
    [
        // items-stretch (default): pills fill the content box; py_* is the inset from the border
        'group/toggle-group inline-flex w-fit',
        'rounded-full border-2 box-border',
        'light:border-gz-marine-700 dark:border-gz-blue-500',
        'gap-1',
    ].join(' '),
    {
        variants: {
            size: {
                sm: 'h-9 px-1 py-1',
                default: 'h-[42px] px-[5px] py-[4px]',
                lg: 'h-12 px-1.5 py-1.5',
            },
        },
        defaultVariants: {
            size: 'default',
        },
    },
);

const toggleGroupItemVariants = cva(
    [
        'inline-flex flex-1 items-center justify-center gap-2',
        'rounded-full type-body-default whitespace-nowrap leading-none',
        'transition-[color,background-color,box-shadow]',
        'outline-none',
        'focus-visible:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
        'light:focus-visible:outline-gz-marine-700 dark:focus-visible:outline-white',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:pointer-events-none [&_svg:not([class*=\'size-\'])]:size-4 [&_svg]:shrink-0',
        // Unselected
        'light:text-gz-neutral-900 dark:text-white',
        'light:hover:bg-gz-marine-100 dark:hover:bg-gz-blue-500/20',
        // Selected
        'light:data-[state=on]:bg-gz-marine-700 light:data-[state=on]:text-white',
        'dark:data-[state=on]:bg-gz-blue-500 dark:data-[state=on]:text-white',
        'light:data-[state=on]:hover:bg-gz-marine-800 dark:data-[state=on]:hover:bg-gz-blue-600',
    ].join(' '),
    {
        variants: {
            size: {
                sm: 'px-2.5',
                default: 'px-3',
                lg: 'px-4',
            },
        },
        defaultVariants: {
            size: 'default',
        },
    },
);

const ToggleGroupContext = React.createContext<
    VariantProps<typeof toggleGroupVariants>
>({
    size: 'default',
});

function ToggleGroup({
    className,
    size,
    children,
    ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleGroupVariants>) {
    return (
        <ToggleGroupPrimitive.Root
            data-slot="toggle-group"
            data-size={size}
            className={cn(toggleGroupVariants({ size }), className)}
            {...props}
        >
            <ToggleGroupContext.Provider value={{ size }}>
                {children}
            </ToggleGroupContext.Provider>
        </ToggleGroupPrimitive.Root>
    );
}

function ToggleGroupItem({
    className,
    size,
    children,
    ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleGroupItemVariants>) {
    const context = React.useContext(ToggleGroupContext);

    return (
        <ToggleGroupPrimitive.Item
            data-slot="toggle-group-item"
            data-size={context.size || size}
            className={cn(
                toggleGroupItemVariants({
                    size: context.size || size,
                }),
                className,
            )}
            {...props}
        >
            {children}
        </ToggleGroupPrimitive.Item>
    );
}

export { ToggleGroup, ToggleGroupItem };
